import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useLocalStorage } from 'usehooks-ts';
import { RegexExpressions } from 'shared/constants/regex/regex-expressions';
import { setLoggingIn } from 'shared/features/auth/authentication/utils/logging-in-util';

import type { LoginFormInput } from 'features/login/models/login-form-input';
import { usePreValidateIdpMutation } from 'features/login/queries/login-queries';

type LoginForm = {
  control: ReturnType<typeof useForm<LoginFormInput>>['control'];
  handleSubmit: ReturnType<typeof useForm<LoginFormInput>>['handleSubmit'];
  formState: ReturnType<typeof useForm<LoginFormInput>>['formState'];
  getRegisterOptions: () => {
    email: {
      required: string;
      validate: (value: string) => true | string;
    };
  };
  login: (loginFormInput: LoginFormInput) => void;
  loginIsLoading: boolean;
  lastEnteredEmailDomain: string;
  isMaintenance: boolean;
};

// HER-9393: Maintenance check disabled for now
// const maintenanceHttpStatusCode = 999;
// const maintenanceCheckIntervalMs = 60000;

export const useLoginForm = (): LoginForm => {
  const { t } = useTranslation('login');
  const { signinRedirect } = useAuth();

  const { control, handleSubmit, formState, setValue, setError } = useForm<LoginFormInput>({
    mode: 'onBlur',
  });

  const [domain, setDomain] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isMaintenance] = useState(false);

  const [lastEnteredEmailDomain, setLastEnteredEmailDomain] = useLocalStorage<string>('last-entered-email-domain', '');

  const {
    mutate: executeValidateIdpMutation,
    isError: loginIsError,
    // error: preValidateError,
    isSuccess: validateIdpMutationIsSuccess,
    isPending: loginIsLoading,
  } = usePreValidateIdpMutation();

  // HER-9393 - Maintenance check disabled for now
  // const {
  //   error: healthCheckError,
  //   refetch: callHealthCheck,
  //   isSuccess: healthCheckIsSuccess,
  //   isPending: healthCheckIsPending,
  // } = useHealthCheck();

  const isFirstRender = useRef(true);
  /* HER-9393 - Maintenance check disabled for now
  useInterval(() => {
    if (isMaintenance) {
      callHealthCheck();
    }
  }, maintenanceCheckIntervalMs);

  useEffect(() => {
    if (healthCheckIsPending) {
      return;
    }

    if (healthCheckIsSuccess) {
      setIsMaintenance(false);
      return;
    }

    const healthCheckStatusCode = (healthCheckError as AxiosError)?.status;
    const preValidateStatusCode = (preValidateError as AxiosError)?.status;

    setIsMaintenance(
        healthCheckStatusCode === maintenanceHttpStatusCode ||
        preValidateStatusCode === maintenanceHttpStatusCode,
    );
  }, [healthCheckError, preValidateError, healthCheckIsSuccess]);
*/
  const validateEmail = (value: string) => {
    const regex = new RegExp(RegexExpressions.EMAIL);
    return regex.test(value?.trim()) || t('emailInputHelperText');
  };

  const getRegisterOptions = () => ({
    email: {
      required: t('emailInputHelperText'),
      validate: validateEmail,
    },
  });

  const login = (loginFormInput: LoginFormInput) => {
    const domainFromEmail = loginFormInput.email.trim().split('@')[1];

    setEmail(loginFormInput.email.trim());
    setDomain(domainFromEmail);
    setLastEnteredEmailDomain(domainFromEmail);

    executeValidateIdpMutation(domainFromEmail);
  };

  useEffect(() => {
    if (loginIsError) {
      setError('email', { type: 'custom', message: t('emailInputHelperText') });
    }
  }, [loginIsError, setError, t]);

  useEffect(() => {
    if (isFirstRender.current && lastEnteredEmailDomain?.length > 0) {
      setValue('email', `@${lastEnteredEmailDomain}`);
      isFirstRender.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEnteredEmailDomain]);

  useEffect(() => {
    if (!loginIsError && validateIdpMutationIsSuccess && !loginIsLoading && email && domain) {
      setLoggingIn();
      signinRedirect({
        login_hint: email,
        extraQueryParams: {
          kc_idp_hint: domain,
        },
      });
    }
  }, [validateIdpMutationIsSuccess, loginIsError, loginIsLoading, email, domain, signinRedirect]);

  return {
    control,
    handleSubmit,
    formState,
    getRegisterOptions,
    login,
    loginIsLoading,
    lastEnteredEmailDomain,
    isMaintenance,
  };
};
